import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { SuggestionCard } from './SuggestionCard'
import { SuggestionCardSkeleton } from './SuggestionCardSkeleton'
import type { Suggestion } from '@/services/api/suggestions'
import { Music } from 'lucide-react'

interface SuggestionListProps {
  suggestions?: Suggestion[]
  isLoading: boolean
  error: Error | null
  onEdit: (suggestion: Suggestion) => void
  onDelete: (suggestion: Suggestion) => void
}

export const SuggestionList = ({
  suggestions,
  isLoading,
  error,
  onEdit,
  onDelete,
}: SuggestionListProps) => {
  if (isLoading) {
    return (
      <div className="grid gap-6 md:gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <SuggestionCardSkeleton key={index} />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load suggestions. Please try again later.</AlertDescription>
      </Alert>
    )
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center">
        <Music className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mb-4" />
        <h3 className="text-base sm:text-lg font-semibold mb-2">No suggestions yet</h3>
        <p className="text-sm text-muted-foreground max-w-md px-4">
          Start suggesting pieces you'd like to play by clicking the "Suggest a Piece" button above.
        </p>
      </div>
    )
  }

  return (
    <div className="grid gap-6 md:gap-4 md:grid-cols-2 lg:grid-cols-3">
      {suggestions.map((suggestion) => (
        <SuggestionCard
          key={suggestion.suggestion_id}
          suggestion={suggestion}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
